import { relations } from 'drizzle-orm'
import { index, integer, pgEnum, pgTable, text } from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { users } from './user'

export const aiMessageRoleEnum = pgEnum('ai_message_role', [
  'user',
  'assistant',
])

// An assistant message is inserted as `pending` by the API and taken over by
// the worker Lambda, which moves it to `running`, appends steps as it goes and
// ends in `done` or `failed`. The ERP polls the conversation while a message is
// not yet done.
export const aiMessageStatusEnum = pgEnum('ai_message_status', [
  'pending',
  'running',
  'done',
  'failed',
  // Stopped by the asker; the worker notices between rounds and leaves the
  // row alone.
  'cancelled',
])

export type AiChartSpec = {
  type: 'bar' | 'line' | 'area' | 'donut'
  title: string
  xKey: string
  series: Array<{ key: string; label: string }>
  data: Array<Record<string, string | number | null>>
  unit?: string
}

// One tool call the assistant made while answering. SQL steps are the audit
// trail of what was read from production; charts are rendered by the ERP.
export type AiStep =
  | {
      type: 'sql'
      purpose: string
      sql: string
      truncated: boolean
      durationMs: number
      columns: string[]
      rows: Array<Record<string, unknown>>
      error?: string
    }
  | { type: 'describe'; tables: string[] }
  | { type: 'chart'; spec: AiChartSpec }
  // A download the model offers: the ERP shows a button that re-runs the
  // query for an Excel file.
  | { type: 'excel'; title: string; sql: string; columns: string[] }
  // Next questions offered as chips under the answer.
  | { type: 'suggestions'; questions: string[] }
  // A hiccup while answering (retry, empty round, the final failure): the
  // code is what a reader quotes to the tech team, detail is the raw text.
  | { type: 'notice'; code: string; text: string; detail?: string; at: string }

export type AiSqlStep = Extract<AiStep, { type: 'sql' }>

export type AiUsage = {
  inputTokens: number
  outputTokens: number
  thoughtTokens: number
  rounds: number
}

export const aiConversations = pgTable(
  'ai_conversations',
  {
    ...getBaseFields(),
    userId: getIdReference(() => users.id),
    title: text().notNull(),
  },
  (table) => [
    ...getBaseIndexes('ai_conversations', table),
    index('ai_conversations_user_id_idx').on(table.userId),
  ],
)

export const aiMessages = pgTable(
  'ai_messages',
  {
    ...getBaseFields(),
    conversationId: getIdReference(() => aiConversations.id),
    role: aiMessageRoleEnum().notNull(),
    // The question for a user message; the final markdown answer for an
    // assistant message (empty until done).
    content: text().notNull().default(''),
    status: aiMessageStatusEnum().notNull().default('done'),
    steps: typedJsonb<AiStep[]>('steps').notNull().default([]),
    error: text(),
    model: text(),
    usage: typedJsonb<AiUsage>('usage'),
    durationMs: integer(),
    completedAt: getTimestampWithTimezone(),
  },
  (table) => [
    ...getBaseIndexes('ai_messages', table),
    index('ai_messages_conversation_id_idx').on(table.conversationId),
  ],
)

export const aiConversationsRelations = relations(
  aiConversations,
  ({ one, many }) => ({
    user: one(users, {
      fields: [aiConversations.userId],
      references: [users.id],
    }),
    messages: many(aiMessages),
  }),
)

export const aiMessagesRelations = relations(aiMessages, ({ one }) => ({
  conversation: one(aiConversations, {
    fields: [aiMessages.conversationId],
    references: [aiConversations.id],
  }),
}))
